import { useEffect, useRef, type ReactNode } from "react";
import { ApiError, send } from "../api.js";
import type { NoteDomain } from "@wmf/shared/studentEngagement";
import { trackUsage } from "../analytics/usage.js";

export interface FamilyNoteIdentity {
  id: string;
  sourceId?: string;
  domain?: NoteDomain;
  updatedAt?: string;
}

export function familyNoteIdentity(note: FamilyNoteIdentity) {
  const domain = note.domain ?? (note.id.startsWith("school:") ? "school" : "private");
  const id = note.sourceId ?? note.id.replace(/^(private|school):/, "");
  return { domain, id, updatedAt: note.updatedAt };
}

/** A disclosure or individual note must be visible in an active tab for 2s. */
export function NoteVisibility({ studentId, note, enabled = true, children }: {
  studentId: string; note: FamilyNoteIdentity; enabled?: boolean; children: ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const identity = familyNoteIdentity(note);
  useEffect(() => {
    const node = host.current;
    if (!node || !enabled || !identity.updatedAt || typeof IntersectionObserver === "undefined") return;
    let visible = false;
    let sent = false;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let eventKey: string | undefined;
    const path = `/engagement/family/students/${encodeURIComponent(studentId)}/notes/${identity.domain}/${encodeURIComponent(identity.id)}/views`;
    const record = async (attempt = 0) => {
      try {
        eventKey ??= crypto.randomUUID();
        await send("POST", path, { eventKey, noteUpdatedAt: identity.updatedAt });
      } catch (error) {
        if (!disposed && attempt < 2 && (!(error instanceof ApiError) || error.status >= 500)) {
          retry = setTimeout(() => void record(attempt + 1), (attempt + 1) * 1500);
        }
        // Recording is independent of displaying the lesson note.
      }
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!sent && visible && document.visibilityState === "visible") {
        timer = setTimeout(() => {
          if (disposed || !visible || document.visibilityState !== "visible") return;
          sent = true;
          trackUsage("lesson_note_view");
          void record();
        }, 2000);
      }
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting && entry.intersectionRect.height >= Math.min(120, entry.boundingClientRect.height / 2));
      schedule();
    }, { threshold: [0, 0.1, 0.5, 1] });
    // A long write-up can reveal 120px without crossing a ratio threshold.
    // Recheck geometry on scroll so those real displays are counted too.
    const onViewportChange = () => {
      const rect = node.getBoundingClientRect();
      const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const next = rect.height > 0 && width > 0 && height >= Math.min(120, rect.height / 2);
      if (next !== visible) { visible = next; schedule(); }
    };
    observer.observe(node);
    document.addEventListener("visibilitychange", schedule);
    window.addEventListener("scroll", onViewportChange, { capture: true, passive: true });
    window.addEventListener("resize", onViewportChange);
    return () => {
      disposed = true;
      clearTimeout(timer); clearTimeout(retry);
      observer.disconnect();
      document.removeEventListener("visibilitychange", schedule);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [studentId, identity.domain, identity.id, identity.updatedAt, enabled]);
  return <div ref={host}>{children}</div>;
}
